import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { useLanguage } from '../context/LanguageContext';

const Hero = () => {
  const { language, t } = useLanguage();
  const tankerRef = useRef(null);
  const tankerMobileRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const refs = [tankerRef.current, tankerMobileRef.current].filter(Boolean);
    
    refs.forEach(ref => {
      gsap.fromTo(
        ref,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1.2,
          ease: 'power3.out',
          delay: 0.5,
        }
      );

      gsap.to(ref, {
        y: -10,
        duration: 4,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
        delay: 1.7,
      });
    });
  }, []);

  return (
    <section 
      ref={containerRef}
      className="relative lg:min-h-screen overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-watrly-cream via-watrly-sand-light to-watrly-mint-light/30" />
      
      {/* Decorative blurs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -right-32 w-96 h-96 bg-watrly-turquoise/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 -left-32 w-80 h-80 bg-watrly-aqua/30 rounded-full blur-3xl" />
      </div>

      {/* Content Section */}
      <div className="relative z-10 flex-1 flex flex-col justify-center pt-20 md:pt-24 lg:pt-0">
        <div className="container-watrly">
          {/* Desktop: Grid layout */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-8 items-center min-h-[calc(100vh-6rem)]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-2xl"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-watrly-turquoise/10 border border-watrly-turquoise/20 mb-6"
              >
                <span className="w-2 h-2 rounded-full bg-watrly-turquoise animate-pulse" />
                <span className="text-sm font-medium text-watrly-turquoise">
                  {t.hero.badge}
                </span>
              </motion.div>

              <h1 className="text-display-lg font-bold text-watrly-slate tracking-tight mb-6 text-balance">
                {t.hero.title}{' '}
                <span className="gradient-text">{t.hero.titleHighlight}</span>{' '}
                {t.hero.titleEnd}
              </h1>

              <p className="text-xl text-watrly-slate/70 leading-relaxed mb-8 max-w-xl">
                {t.hero.subtitle}
              </p>

              <div className="flex flex-row gap-4">
                <motion.a
                  href="#contact"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-primary"
                >
                  {t.hero.cta}
                  <svg className={`${language === 'ar' ? 'mr-2 rotate-180' : 'ml-2'} w-5 h-5`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </motion.a>
                <motion.a
                  href="#how-it-works"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-secondary"
                >
                  {t.hero.ctaSecondary}
                </motion.a>
              </div>

              <div className="mt-10 pt-8 border-t border-watrly-slate/10">
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <div className="text-3xl font-bold text-watrly-turquoise">18-32</div>
                    <div className="text-sm text-watrly-slate/60 mt-1">{t.hero.statCapacity}</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-watrly-turquoise">{t.hero.statLive}</div>
                    <div className="text-sm text-watrly-slate/60 mt-1">{t.hero.statTracking}</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-watrly-turquoise">24/7</div>
                    <div className="text-sm text-watrly-slate/60 mt-1">{t.hero.statOperations}</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="relative flex items-center justify-end">
              <div 
                ref={tankerRef}
                className="relative w-full opacity-0"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-watrly-turquoise/20 to-watrly-aqua/20 rounded-full blur-3xl scale-75" />
                <img
                  src="/assets/images/watrly-tank.png"
                  alt="Watrly branded water tanker"
                  className="relative z-10 w-full h-auto drop-shadow-2xl"
                />
              </div>
            </div>
          </div>

          {/* Mobile: Professional platform design */}
          <div className="lg:hidden relative">
            {/* Background Image Container */}
            <div className="relative">
              {/* Image sets the height */}
              <div ref={tankerMobileRef} className="opacity-0">
                <img
                  src="/assets/images/watrly-tank.png"
                  alt="Watrly branded water tanker"
                  className="w-full h-auto"
                />
              </div>
              
              {/* Professional gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-watrly-cream via-watrly-cream/90 via-55% to-watrly-mint-light/60" />
              
              {/* Content - Centered horizontally and vertically */}
              <div className="absolute inset-0 flex flex-col items-center justify-center px-6 py-6">
                {/* All content wrapper - centered */}
                <div className="w-full max-w-sm flex flex-col items-center text-center">
                  {/* Badge */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-4"
                  >
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-watrly-turquoise/15 border border-watrly-turquoise/25 text-xs font-semibold text-watrly-turquoise">
                      <span className="w-1.5 h-1.5 rounded-full bg-watrly-turquoise animate-pulse" />
                      {t.hero.badge}
                    </span>
                  </motion.div>

                  {/* Title - SemiBold with better line-height */}
                  <motion.h1
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.5 }}
                    className="text-xl font-semibold text-watrly-slate tracking-tight mb-3 leading-relaxed"
                  >
                    {t.hero.title} <span className="gradient-text font-bold">{t.hero.titleHighlight}</span> {t.hero.titleEnd}
                  </motion.h1>

                  {/* Subtitle - Better spacing */}
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="text-sm text-watrly-slate/70 mb-5 leading-relaxed"
                  >
                    {t.hero.subtitle}
                  </motion.p>

                  {/* CTA Buttons - Centered */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="flex gap-3 w-full"
                  >
                    <a href="#contact" className="flex-1 btn-primary text-sm py-3 justify-center font-semibold shadow-lg">
                      {t.hero.cta}
                      <svg className={`${language === 'ar' ? 'mr-2 rotate-180' : 'ml-2'} w-4 h-4`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </a>
                    <a href="#how-it-works" className="flex-1 btn-secondary text-sm py-3 justify-center font-medium bg-white/95 shadow-sm">
                      {t.hero.ctaSecondary}
                    </a>
                  </motion.div>

                  {/* Stats Row */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                    className="grid grid-cols-3 w-full mt-5 py-3 bg-white/85 backdrop-blur-md rounded-xl border border-watrly-slate/10 shadow-md"
                  >
                    <div className="text-center border-r border-watrly-slate/10">
                      <div className="text-lg font-bold text-watrly-turquoise">18-32</div>
                      <div className="text-[10px] text-watrly-slate/60 font-medium">{t.hero.statCapacity}</div>
                    </div>
                    <div className="text-center border-r border-watrly-slate/10">
                      <div className="text-lg font-bold text-watrly-turquoise">{t.hero.statLive}</div>
                      <div className="text-[10px] text-watrly-slate/60 font-medium">{t.hero.statTracking}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-watrly-turquoise">24/7</div>
                      <div className="text-[10px] text-watrly-slate/60 font-medium">{t.hero.statOperations}</div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2"
      >
        <span className="text-xs font-medium text-watrly-slate/40 uppercase tracking-widest">
          {t.hero.scroll}
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          className="w-6 h-10 rounded-full border-2 border-watrly-slate/20 flex items-start justify-center p-2"
        >
          <div className="w-1.5 h-1.5 rounded-full bg-watrly-turquoise" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
